import { Card } from '../../../molecules/Card/Card';
import type { CardHeaderTabs } from '../../../molecules/CardHeader/CardHeader';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import {
  PLOT_HEIGHT,
  cardState,
  spanClass,
  ticksFor,
  type WidgetBlockProps,
} from '../shared/cardShell';

export type TrendLinesView = {
  /** Figures above the plot; none draws no KPI row. */
  kpis: KPIMetric[];
  /** One row per bucket, one key per series. */
  rows: ChartRow[];
  xKey: string;
  /** The lines drawn, each in its own entity's colour. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type TrendLinesCardProps = WidgetBlockProps & {
  view?: TrendLinesView;
  title: string;
  helpText: string;
  helpSupportingText?: string;
  /** A switch between views of the same card (`Growing` / `Declining`). */
  tabs?: CardHeaderTabs;
  /** The body when nothing is drawn — say why, where it is not just an empty period. */
  emptyLabel?: string;
  /** Opens the conversations behind a bucket (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * A few lines over time with a legend, under the executive view's own title —
 * Customer voice's topic and intent trends (GP, 2026-11-05). The lines are
 * counts, not indexed: a topic's weekly mentions are what a reader compares.
 */
export function TrendLinesCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  title,
  helpText,
  helpSupportingText,
  tabs,
  emptyLabel,
  onViewDetails,
}: TrendLinesCardProps) {
  const hasData = view !== undefined && view.series.length > 0 && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title={title}
      helpText={helpText}
      helpSupportingText={helpSupportingText}
      tabs={tabs}
      emptyLabel={emptyLabel}
      kpi={view && view.kpis.length > 0 ? <KPI metrics={view.kpis} /> : undefined}
      chart={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            xTicksTarget={ticksFor(span)}
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
